import { CLEAR_LENS, defaultAppearance, lensOnly, LENS_TINTS, SPECIAL_LENSES, type AppearanceChoice } from '../config/appearance';
import type { FrameSpec } from '../config/frames';
import type { SizingSelection } from '../pose/SizingSession';
import { HANDMADE_SIZES } from '../pose/Sizing';
import type { EngraveSummary } from '../engrave/EngraveSession';
import type { SlotKey } from '../engrave/state';
import { acetateName } from '../ui/AppearanceControls';
import { words } from '../ui/words';
import { acetateRef } from './acetateRef';
import type { EngravingOrder, OrderCopy, OrderSnapshot } from './orderSpecs';

/** Copy of the order helpers in the current UI language. */
export function orderCopy(): OrderCopy {
  const w = words();
  return { order: w.order, original: w.original, intensity: w.intensity, engrave: w.engrave };
}

/**
 * A110Z: the engraving as the order carries it. A110Z2: every written text carries its letter height
 * (« lettres 2,5 mm », as drawn on the model). A110Z3: a text placed by hand also carries where and how
 * (« centre à 24 mm de la charnière, 0,5 mm plus bas, incliné de 8° vers le haut ») — the order photo shows it
 * on the temples themselves.
 */
export function engravingOrder(summary: EngraveSummary | null): EngravingOrder | null {
  if (!summary) return null;
  const w = words().engrave;
  const inkId = (slot: SlotKey) => summary.inks?.[slot]?.id ?? summary.ink.id;
  /**
   * A201 (David : « il me faut toutes les infos des textes ») : every view, always — its colour, its letter
   * height (mm) and where it is (from the hinge, across, tilt), placed by hand or not. An empty view says so.
   */
  const detail = (slot: SlotKey, cap: number | undefined): string => {
    const parts = [w.inks[inkId(slot) as keyof typeof w.inks]];
    if (cap) parts.push(w.capOrder(w.mm(cap)));
    const place = summary.placesMm?.[slot];
    if (place) parts.push(w.placeOrder(place.x, place.y, place.rot));
    if (summary.movedByHand?.includes(slot)) parts.push(w.moved);
    return parts.join(' · ');
  };
  const texts = (['ir', 'el', 'er'] as const).map(slot => {
    const p = summary.payload.slots.find(x => x.slot === slot);
    const text = p?.text ?? '';
    return { slot, label: w.slots[slot], text, detail: text ? detail(slot, summary.capsMm?.[slot] ?? p?.cap) : null };
  });
  const logo = { slot: 'il' as const, label: w.slots.il, text: [w.faceLogo, summary.payload.logo.suffix].filter(Boolean).join(' · '), detail: detail('il', summary.capsMm?.il ?? summary.payload.logo.cap) };
  // One colour for every engraved view, or each one named.
  const used = [logo, ...texts].filter(v => v.slot === 'il' || v.text).map(v => w.inks[inkId(v.slot) as keyof typeof w.inks]);
  const inkName = [...new Set(used)].join(' / ');
  return { fontName: summary.font.name, inkName, texts: summary.texts, slots: [logo, ...texts] };
}

/** Display name of a lens of the Colours panel (null for « Original »). */
export function lensName(id: string): string | null {
  if (id === 'original') return null;
  if (LENS_TINTS.some(l => l.id === id) || id === CLEAR_LENS.id) return words().lenses[id] ?? id;
  return SPECIAL_LENSES.find(l => l.id === id)?.label ?? id;
}

/**
 * What the customer sees in the try-on right now: the current model, its
 * Colours choice (null when the frame has no material preview) and the Size
 * panel selection (size letter for Handmade frames, PD estimated or typed in).
 */
export function buildOrderSnapshot(spec: FrameSpec, choice: AppearanceChoice | null, sizing: SizingSelection, engraving: EngraveSummary | null = null): OrderSnapshot {
  const handmade = spec.line === 'Handmade';
  const original = choice?.acetate === 'original';
  return {
    frameId: spec.id,
    model: spec.model,
    colorway: spec.colorway,
    handle: spec.productHandle,
    materials: choice && {
      acetateId: choice.acetate,
      acetateName: original ? null : acetateName(choice.acetate) ?? choice.acetate,
      acetateRef: original ? null : acetateRef(choice.acetate),
      lensId: choice.lens,
      lensName: lensName(choice.lens),
      density: choice.density,
      gradient: choice.gradient,
      defaultGradient: defaultAppearance(spec).gradient,
      ...(lensOnly(spec) ? { lensOnly: true } : {}),
    },
    size: handmade
      ? { label: HANDMADE_SIZES.find(s => s.widthMm === sizing.width)?.label ?? null, widthMm: sizing.width }
      : { label: null, widthMm: Number.isFinite(spec.dims.widthMm) && spec.dims.widthMm > 0 ? Math.round(spec.dims.widthMm) : null },
    pd: { mm: sizing.pd, estimated: !sizing.manual },
    engraving: engravingOrder(engraving),
  };
}
